import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useRef, useState } from 'react';
import { Box, FullRow, Menu, Popover, Row, TextButton, Typography } from 'seagull';
import { ChevronDown } from 'seagull/icons';
import { PlusCircle } from 'seagull/icons/essentials';
import { getTestIds } from 'seagull/utils/TestIds';
import { agreementOptionKey } from '~/components/BillModal/components/BillItemsTable/ItemServiceSelect/itemServiceSelect.utils';
import { useLocaleKeys } from '~/i18n';
import styles from './AddCharge.module.scss';
import { toGroupedMenuItems } from './addCharge.utils';
export const GetAddChargeTID = (parentTestId)=>getTestIds(parentTestId);
export const AddCharge = (param)=>{
    let { agreementOptions, outOfScopeTemplates, formatTemplateCost, triggerLabel, triggerVariant = 'link', flat = false, onSelect, onCreateNew, bi, testId } = param;
    const { t: addChargeTexts } = useLocaleKeys('components.billClientModal.addCharge');
    const { AddChargeTestIds } = useMemo(()=>GetAddChargeTID(testId), [
        testId
    ]);
    const triggerRef = useRef(null);
    const [isPickerVisible, setPickerVisible] = useState(false);
    const selectionsById = useMemo(()=>{
        const map = new Map();
        agreementOptions.forEach((option)=>map.set(agreementOptionKey(option), {
                kind: 'agreementService',
                option
            }));
        outOfScopeTemplates.forEach((template)=>map.set(template.id, {
                kind: 'outOfScopeTemplate',
                template
            }));
        return map;
    }, [
        agreementOptions,
        outOfScopeTemplates
    ]);
    const items = useMemo(()=>{
        const mapped = toGroupedMenuItems({
            agreementOptions,
            outOfScopeTemplates,
            texts: {
                inAgreementSectionLabel: addChargeTexts.inAgreementSection(),
                outOfScopeSectionLabel: addChargeTexts.outOfScopeSection()
            },
            formatTemplateCost,
            flat
        });
        return mapped.map((item)=>{
            if (item.isSectionHeader) {
                return item;
            }
            const selection = selectionsById.get(item.id);
            return _object_spread_props(_object_spread({}, item), {
                onClick: ()=>{
                    if (selection) {
                        onSelect(selection);
                        setPickerVisible(false);
                    }
                }
            });
        });
    }, [
        agreementOptions,
        outOfScopeTemplates,
        formatTemplateCost,
        addChargeTexts,
        selectionsById,
        onSelect,
        flat
    ]);
    const dropdownTrigger = /*#__PURE__*/ _jsx(TextButton, {
        ref: triggerRef,
        underline: "none",
        skin: "dark",
        bi: bi.trigger,
        testId: AddChargeTestIds.Trigger(),
        className: styles.dropdownTrigger,
        onClick: ()=>setPickerVisible(true),
        children: /*#__PURE__*/ _jsxs(FullRow, {
            gap: 4,
            alignItems: "center",
            justifyContent: "space-between",
            children: [
                /*#__PURE__*/ _jsx(Typography, {
                    variant: "paragraph2",
                    ellipsis: true,
                    children: triggerLabel
                }),
                /*#__PURE__*/ _jsx(Box, {
                    as: "span",
                    testId: AddChargeTestIds.Chevron(),
                    className: styles.chevron,
                    children: /*#__PURE__*/ _jsx(ChevronDown, {
                        size: 16
                    })
                })
            ]
        })
    });
    const dashedBoxTrigger = /*#__PURE__*/ _jsx(TextButton, {
        ref: triggerRef,
        underline: "none",
        skin: "primary",
        bi: bi.trigger,
        testId: AddChargeTestIds.Trigger(),
        className: styles.dashedBoxTrigger,
        onClick: ()=>setPickerVisible(true),
        children: /*#__PURE__*/ _jsxs(Row, {
            gap: 8,
            alignItems: "center",
            children: [
                /*#__PURE__*/ _jsx(Box, {
                    as: "span",
                    testId: AddChargeTestIds.DashedBoxIcon(),
                    className: styles.dashedBoxIcon,
                    children: /*#__PURE__*/ _jsx(PlusCircle, {
                        size: 20
                    })
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    variant: "paragraph2",
                    weight: "bold",
                    children: triggerLabel
                })
            ]
        })
    });
    const linkTrigger = /*#__PURE__*/ _jsx(TextButton, {
        ref: triggerRef,
        underline: "none",
        bi: bi.trigger,
        testId: AddChargeTestIds.Trigger(),
        onClick: ()=>setPickerVisible(true),
        children: triggerLabel
    });
    const triggersByVariant = {
        dropdown: dropdownTrigger,
        dashedBox: dashedBoxTrigger,
        link: linkTrigger
    };
    const trigger = triggersByVariant[triggerVariant];
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            trigger,
            /*#__PURE__*/ _jsx(Popover, {
                open: isPickerVisible,
                anchorEl: triggerRef.current,
                onClose: ()=>setPickerVisible(false),
                bottomSheetHeader: {
                    title: addChargeTexts.bottomSheetTitle()
                },
                children: /*#__PURE__*/ _jsx(Menu, {
                    testId: AddChargeTestIds.Menu(),
                    items: items,
                    emptyText: addChargeTexts.emptyState(),
                    searchProps: {
                        showSearch: true,
                        autoFocus: true,
                        placeholder: addChargeTexts.searchPlaceholder(),
                        strategy: {
                            type: 'text'
                        },
                        bi: bi.search
                    },
                    footer: {
                        items: [
                            {
                                variant: 'contextualAction',
                                props: {
                                    title: addChargeTexts.createNew(),
                                    testId: AddChargeTestIds.CreateNew(),
                                    bi: bi.createNew,
                                    onClick: ()=>{
                                        onCreateNew();
                                        setPickerVisible(false);
                                    }
                                }
                            }
                        ]
                    }
                })
            })
        ]
    });
};
